/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2023 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Chatbot script
 *
 * @author Mohamed yasar arabath M
 */

import PathUtils from './PathUtils';
import { GET_HELP, GET_HELP_CHATBOT_ID, POST_MESSAGE_TYPE } from '../constants';
import env from '../env';

/* eslint-disable */
function updateChatBotSettings(settings) {
  const targetElement = document.querySelector(`#${GET_HELP_CHATBOT_ID}`);
  settings.targetElement = targetElement || document.body;
}

/**
 * Enhanced Web Chat (`embeddedservice_bootstrap`): honor `?openChat=true` by maximizing the
 * conversation window once the default chat button exists.
 *
 * Call {@link https://developer.salesforce.com/docs/service/messaging-web/guide/launch-chat.html | utilAPI.launchChat}
 * only after {@link https://developer.salesforce.com/docs/service/messaging-web/guide/event-listeners.html | onEmbeddedMessagingButtonCreated}.
 * Using `onEmbeddedMessagingReady` causes "Default chat button isn't present" because the button
 * is not in the DOM yet. Register the listener before `embeddedservice_bootstrap.init(...)`.
 */
function registerEmbeddedMessagingOpenChatFromQueryParam() {
  if (PathUtils.getQueryParameterbyName('openChat') !== 'true') {
    return;
  }

  window.addEventListener(
    'onEmbeddedMessagingButtonCreated',
    () => {
      window?.embeddedservice_bootstrap?.utilAPI
        ?.launchChat()
        .catch((err) => console.error('Embedded Messaging launchChat (openChat): ', err));
    },
    { once: true }
  );
}

const initESW = (gslbBaseURL, config, enableNewChatbot) => {
  if (!enableNewChatbot) {
    window.embedded_svc.settings.displayHelpButton = true; // Or false
    window.embedded_svc.settings.language = ''; // For example, enter 'en' or 'en-US'
    updateChatBotSettings(window.embedded_svc.settings);

    // Dynamically changes the button ID based on what the visitor enters in the pre-chat form. // Returns a valid button ID. //}
    window.embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    window.embedded_svc.settings.entryFeature = 'LiveAgent';
    window.embedded_svc.settings.disabledMinimizedText = GET_HELP;
    window.embedded_svc.settings.defaultMinimizedText = GET_HELP;
    window.embedded_svc.settings.loadingText = GET_HELP;
    window.embedded_svc.init(
      config.baseUrl,
      config.supportUrl,
      gslbBaseURL,
      config.id,
      config.name,

      {
        baseLiveAgentContentURL: config.baseLiveAgentContentURL,
        deploymentId: config.deploymentId,
        buttonId: config.buttonId,
        baseLiveAgentURL: config.baseLiveAgentURL,
        eswLiveAgentDevName: config.eswLiveAgentDevName,
        isOfflineSupportEnabled: config.isOfflineSupportEnabled
      }
    );

    if(window.embedded_svc) {
      window.embedded_svc.addEventHandler('afterInit', ()=> {
        if(PathUtils.getQueryParameterbyName('openChat') === 'true') {
          window.embedded_svc.onHelpButtonClick()
        }
        const checkChatBotExist = setInterval(() => {
          const chatBotElement = document.getElementsByClassName('helpButtonEnabled');
          if (chatBotElement && chatBotElement.length > 0) {
            chatBotElement[0].setAttribute('aria-label', 'Expand chat');
            clearInterval(checkChatBotExist);
          }
        }, 100);
      });
    }
  } else {
    try {
      window.embeddedservice_bootstrap.settings.language = 'en_US';
      updateChatBotSettings(window.embeddedservice_bootstrap.settings);
      window.embeddedservice_bootstrap.init(
        config.authCode,
        config.authKey,
        config.authURL,
        {
          scrt2URL: config.scrt2URL
        }
      );
    } catch (err) {
      console.error('Error loading Embedded Messaging: ', err);
    }

  }
};

// posting message to disable user input
function postIframe() {
  const iframe = document.getElementById('embeddedMessagingFrame');

  if (!iframe) {
    return;
  }

  iframe.contentWindow.postMessage(
    'MIAWDisable',
    env.BOOKSHELF_CONFIG.chatbot.authURL
  );
  console.log('~ In postIframe');
}

// posting message to enable user input
function postIframeEnable() {
  const iframe = document.getElementById('embeddedMessagingFrame');

  if (!iframe) {
    return;
  }

  iframe.contentWindow.postMessage(
    'InputEnable',
    env.BOOKSHELF_CONFIG.chatbot.authURL
  );
  console.log('~ In IframeEnable');
}

// posting message to disable user input
function postIframeDisable() {
  const iframe = document.getElementById('embeddedMessagingFrame');

  if (!iframe) {
    return;
  }

  iframe.contentWindow.postMessage(
    'InputDisable',
    env.BOOKSHELF_CONFIG.chatbot.authURL
  );
  console.log('~ In IframeDisable');
}

/**
 * Function that accepts an event and runs the provided chatbot logic
 * @param {any} event - event object
 */
const handleChatBotIframeEvent = (event = {}) => {
  if (!event.data?.method) {
    return;
  }

  try {
    if (
      event.data.method === POST_MESSAGE_TYPE.CHATBOT
      && event.data.data.eventDetails.conversationEntry
      && event.data.data.eventDetails.conversationEntry.entryPayload
    ) {
      const payload = JSON.parse(
        event.data.data.eventDetails.conversationEntry.entryPayload
      );
      if (payload.messageReason === 'Consent') {
        postIframe();
      }
      if (payload.abstractMessage && payload.abstractMessage.choices) {
        postIframeDisable();
      } else if (payload.abstractMessage && payload.abstractMessage.choicesResponse) {
        postIframeEnable();
      }
    }
  } catch (err) {
    console.error('Error handling chatbot iframe event:', err, event)
}
};

export { initESW, handleChatBotIframeEvent, registerEmbeddedMessagingOpenChatFromQueryParam };
